/*
  <bloom-pdp-sticky> — the mobile add-to-cart bar. Reveals itself once the main
  buy block has scrolled past, and mirrors the live variant: price, the selected
  option values, and the variant image.

  Two things it deliberately does NOT own:
  - the add itself. Its submit button targets the product form (form="…" name="add"),
    so Dawn's product-form.js runs the same AJAX add as the main button.
  - the quantity. The stepper here proxies the real #Quantity-… input rather than
    being a second field named "quantity" in the same form, which would post twice.
*/
class BloomPdpSticky extends HTMLElement {
  connectedCallback() {
    this.info = this.closest('product-info') || document;
    this.anchor = this.info.querySelector('.bloom-pdp__buy') || this.info.querySelector('.bloom-pdp__panel');
    this.priceSource = this.info.querySelector('[id^="price-"]');
    this.priceTarget = this.querySelector('[data-sticky-price]');
    this.image = this.querySelector('.bloom-pdp__stickybar-img');
    this.qtyInput = this.querySelector('[data-sticky-qty-input]');

    this.observeScroll();
    this.syncPrice();
    this.bindQuantity();
    this.bindChange();
    this.watchVariant();
  }

  get mainQuantity() {
    return document.getElementById(this.dataset.quantityId || '');
  }

  observeScroll() {
    if (!this.anchor || !('IntersectionObserver' in window)) return;
    this.io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        // Show only once the buy block has scrolled fully above the viewport.
        const past = entry.boundingClientRect.bottom <= 0;
        this.setVisible(!entry.isIntersecting && past);
      },
      { threshold: 0 }
    );
    this.io.observe(this.anchor);
  }

  setVisible(visible) {
    this.classList.toggle('is-visible', visible);
    this.setAttribute('aria-hidden', visible ? 'false' : 'true');
  }

  bindQuantity() {
    const main = this.mainQuantity;
    if (!this.qtyInput || !main) return;

    this.qtyInput.value = main.value;

    this.querySelectorAll('[data-sticky-qty]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const next = Math.max(1, (parseInt(main.value, 10) || 1) + parseInt(btn.dataset.stickyQty, 10));
        this.pushQuantity(next);
      });
    });

    this.qtyInput.addEventListener('change', () => {
      this.pushQuantity(Math.max(1, parseInt(this.qtyInput.value, 10) || 1));
    });

    // Keep in step when the main stepper is the one being used.
    main.addEventListener('change', () => {
      this.qtyInput.value = main.value;
    });
  }

  // Write through to the real input and fire `change`, so Dawn's quantity-input
  // rules (min / max / increments) get a say before the value is trusted.
  pushQuantity(value) {
    const main = this.mainQuantity;
    if (!main) return;
    main.value = String(value);
    main.dispatchEvent(new Event('change', { bubbles: true }));
    this.qtyInput.value = main.value;
  }

  bindChange() {
    const link = this.querySelector('[data-sticky-change]');
    if (!link) return;
    link.addEventListener('click', () => {
      const picker = this.info.querySelector('.bloom-pdp__variants') || this.anchor;
      if (!picker) return;
      picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
      picker.querySelector('input[type="radio"]:checked')?.focus({ preventScroll: true });
    });
  }

  syncPrice() {
    if (!this.priceSource || !this.priceTarget) return;
    const update = () => {
      const el = this.priceSource.querySelector('.price-item--sale, .price-item--regular, .price-item');
      if (el) this.priceTarget.textContent = el.textContent.trim();
    };
    update();
    // product-info.js replaces the price node's innerHTML rather than editing text.
    this.mo = new MutationObserver(update);
    this.mo.observe(this.priceSource, { childList: true, subtree: true, characterData: true });
  }

  // Option labels and the thumbnail follow the variant, not the DOM, so read them
  // off the variantChange payload instead of scraping the re-rendered picker.
  watchVariant() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      if (!data || data.sectionId !== this.info.dataset?.section) return;
      const variant = data.variant;
      if (!variant) return;

      (variant.options || []).forEach((value, index) => {
        const slot = this.querySelector(`[data-sticky-option="${index + 1}"]`);
        if (slot) slot.textContent = value;
      });

      const media = variant.featured_media;
      if (this.image && media && media.preview_image) {
        this.image.src = media.preview_image.src.replace(/(\.[a-z]+)(\?.*)?$/i, '_160x$1$2');
        this.image.removeAttribute('srcset');
      }
    });
  }

  disconnectedCallback() {
    if (this.io) this.io.disconnect();
    if (this.mo) this.mo.disconnect();
    if (this.unsubscribe) this.unsubscribe();
  }
}

if (!customElements.get('bloom-pdp-sticky')) {
  customElements.define('bloom-pdp-sticky', BloomPdpSticky);
}
